import type { ThreeDotsButtonProps } from './ThreeDotsButton.types';
import { ChatLayoutContext } from 'Components/Chat/contexts/ChatLayoutContext';
import { ConfirmationModalContext } from 'Components/ChatLayoutHeader/contexts/ConfirmationModalContext';
import { IconButton } from 'Components/shared/IconButton';
import { PopOverMenu } from 'Components/shared/PopOverMenu';
import { COMMUNICATOR_REACTIONS } from 'Constants/featureFlags';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { MobXProviderContext } from 'mobx-react';
import { MessageModel } from 'Models/MessageModel';
import { EmojiReactionButtons } from 'Modules/ui/emoji/emojiReactions/components/emojiReactionButtons';
import React from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import ActionList from '~/lib/ui/actionList';

export const ThreeDotsButton: React.FC<ThreeDotsButtonProps> = ({
  conversationId,
  isOwnMessage,
  message,
  openThreeDots,
  setOpenThreeDots,
  setShowThreeDots,
  testid,
  togglePinMessage,
  pinnedBy,
}) => {
  const { message: messageStore } =
    React.useContext<RootStoreProps>(MobXProviderContext);
  const { setShow } = React.useContext(ConfirmationModalContext);
  const { setEditMessageId, setReplyMessage } =
    React.useContext(ChatLayoutContext);
  const { [COMMUNICATOR_REACTIONS]: isReactionsEnabled } = useFeatureFlags([
    COMMUNICATOR_REACTIONS,
  ]);

  const messageFailedToSend =
    message instanceof MessageModel &&
    (message.internalStatus === 'FailedToSend' ||
      message.smsStatus === 'FailedToSend');

  const closeMenu = (callback?: () => void | Promise<void>) => {
    if (callback) {
      void callback();
    }
    setShowThreeDots(false);
    setOpenThreeDots(false);
  };

  const deleteMessage = () => {
    const onConfirm = () =>
      void messageStore.deleteMessage(
        conversationId,
        message.id,
        messageFailedToSend
      );
    setShow({ type: 'deleteMessage', onConfirm });
  };

  const replyMessage = () => {
    setReplyMessage({
      id: message.id,
      type: 'reply',
      snapshot: {
        chat: message.chat,
        created: message.created,
        documents: message.documents,
        personId: message.personId,
        gif: message.gif,
      },
    });
  };

  return (
    <PopOverMenu
      trigger={
        <IconButton
          compact
          icon="more"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setOpenThreeDots(true);
          }}
          testid={`${testid}-buttonMore`}
        />
      }
      content={
        <React.Fragment>
          <ActionList testID={testid}>
            {!message.sms && !messageFailedToSend && (
              <ActionList.Item
                onClick={() => closeMenu(replyMessage)}
                text="Reply"
              />
            )}
            {isOwnMessage && !message.sms && (
              <React.Fragment>
                {!messageFailedToSend && !message.gif?.id && (
                  <ActionList.Item
                    onClick={() => closeMenu(() => setEditMessageId(message.id))}
                    text="Edit"
                  />
                )}
                <ActionList.Item
                  onClick={() => closeMenu(deleteMessage)}
                  text="Delete"
                />
              </React.Fragment>
            )}
            {!messageFailedToSend && (
              <ActionList.Item
                onClick={() => closeMenu(togglePinMessage)}
                text={pinnedBy ? 'Unpin message' : 'Pin message'}
              />
            )}

            {!message.sms && !messageFailedToSend && isReactionsEnabled && (
              <ActionList.Divider />
            )}

            {!message.sms && !messageFailedToSend && isReactionsEnabled && (
              <EmojiReactionButtons
                testID={testid}
                reactions={(message as MessageModel).reactions}
                messageId={message.id}
                closeMenu={closeMenu}
              />
            )}
          </ActionList>
        </React.Fragment>
      }
      show={openThreeDots}
      onOutsideClick={() => closeMenu()}
      position="top right"
      offset={[5, 0]}
      hideOnScroll
    />
  );
};
